import { defineStore } from 'pinia';
import { useUserStore } from './user.store';
import {
  getMessagePageList,
  getUnReadCount,
  allMarkRead,
  markRead,
  ISysMessage,
  IMessageQueryParams,
} from '@/api/system/sysMessage';
import router from '@/router';

async function checkLoginStatus() {
  const token = getToken();
  const userStore = useUserStore();
  const loginPage = useLoginPage();
  if (token === undefined) {
    await userStore.logout();
    router.push(loginPage);
  }
}

export const useSysMessageStore = defineStore('sysMessageStore', () => {
  const unReadList = ref([] as ISysMessage[]);
  const readedList = ref([] as ISysMessage[]);
  const newMessageCount = ref<number>(0);
  const unReadTotal = ref(0);
  const readedTotal = ref(0);
  const queryParam: IMessageQueryParams = {
    current: 1,
    size: 10,
    userId: (useUserStore().userInfo as any).id,
  };

  enum Command {
    /**
     * 需要拉取新的消息
     */
    pullNewMessage = ':!pull new message',
    /**
     * 需要检查登录状态
     */
    checkLoginStatus = ':!check login status',
  }

  const messageList = computed(() => [...unReadList.value, ...readedList.value]);

  /** 加载未读消息数量 */
  async function loadUnReadCount() {
    const { code, data }: any = await getUnReadCount();
    if (code === 200) {
      newMessageCount.value = data;
    }
  }

  /** 获取消息列表
   *
   * @param params 查询参数
   * @returns
   */
  async function getMessageList(params: IMessageQueryParams) {
    const { data }: any = await getMessagePageList({ ...params });
    loadUnReadCount();
    return Promise.resolve(data);
  }

  /** 加载消息列表(初始化) */
  async function initMessageList() {
    unReadList.value = [];
    readedList.value = [];
    await loadMoreUnReadMessage();
    await loadMoreReadedMessage();
  }

  /** 加载最新的消息 */
  async function loadNewMessage() {
    // 加载未读的消息，替换掉未读消息列表
    let params = { ...queryParam, readStatus: '0' };
    if (unReadList.value && unReadList.value.length > 0) {
      params = { ...params, latestMessageIdInClient: unReadList.value[0]?.id };
    }
    const res = await getMessageList(params);
    unReadList.value.unshift(...res.records);
  }

  /** 加载更多消息，追加到已读消息列表 */
  async function loadMoreReadedMessage() {
    await loadMoreMessage('1', readedList);
  }

  /** 加载更多消息，追加到未读消息列表 */
  async function loadMoreUnReadMessage() {
    await loadMoreMessage('0', unReadList);
  }

  /** 加载更多消息，放到指定的列表中最后
   * @param readStatus 已读状态：0-未读，1-已读
   * @param list 处理的列表
   */
  async function loadMoreMessage(readStatus: string, list: any) {
    let params = { ...queryParam, readStatus };
    if (list.value && list.value.length > 0) {
      params = { ...params, oldestMessageIdInClient: list.value[list.value.length - 1]?.id || undefined };
    }
    const res = await getMessageList(params);
    list.value.push(...res.records);
    if (params?.oldestMessageIdInClient === undefined) {
      if (readStatus === '0') unReadTotal.value = res.total || 0;
      else if (readStatus === '1') readedTotal.value = res.total || 0;
    }
  }

  /** 一键已读 */
  async function readAll() {
    await allMarkRead();
    await initMessageList();
  }

  /** 标记已读 */
  async function readOne(messageId: string) {
    const { data } = await markRead(messageId);
    if (data) {
      newMessageCount.value -= 1;
    }
  }

  // #region websocket
  const userStore = useUserStore();
  const wsUrl = computed(() => {
    let url = '';
    const hostname = window.location.hostname;
    const port = window.location.port ? ':' + window.location.port : '';
    const timestamp = new Date().getTime();
    const sessionKey = userStore.userInfo.userId + '_' + timestamp;
    // 协议跟随页面：http→ws，https→wss
    const ws = window.location.protocol === 'https:' ? 'wss' : 'ws';
    url = `${ws}://${hostname}${port}/base-websocket/base-asset/websocket/${sessionKey}`;
    return url;
  });

  const { data, open } = useWebSocket(wsUrl, {
    // heartbeat: { interval: 3000 },
    autoReconnect: {
      retries: 0,
      delay: 5000,
      onFailed() {
        console.error('Failed to connect WebSocket after 3 retries');
      },
    },
  });

  // 收到更新提醒，接收消息
  watch(data, (val: string) => {
    if (!val) return;
    console.debug('command: ', val);
    if (val.startsWith(Command.pullNewMessage)) {
      loadNewMessage();
    } else if (val.startsWith(Command.checkLoginStatus)) {
      checkLoginStatus();
    }
  });
  // #endregion

  // 用户身份变化，初始化消息列表
  watch(
    () => userStore.userInfo.userId,
    async (val) => {
      if (val && val !== '' && val !== 'undefined') {
        await initMessageList();
      }
    },
  );

  onMounted(async () => {
    await initMessageList();
    open();
  });

  return {
    messageList,
    readedList,
    unReadList,
    newMessageCount,
    unReadTotal,
    readedTotal,
    loadNewMessage,
    loadMoreUnReadMessage,
    loadMoreReadedMessage,
    allMarkRead: readAll,
    readOne,
  };
});
